import { api } from '../utils/apiClient';

const API_URL = `/api/ai-reply`;

// Reply generation is a multi-stage LLM pipeline (and batch generates 3
// variants), so a single call can legitimately run well past the apiClient's
// default 90s timeout. Give these endpoints generous headroom so a slow-but-
// successful generation isn't aborted client-side and shown as a failure.
const GENERATE_TIMEOUT_MS = 180_000;

// Comment context for replying to a specific comment
export interface CommentContext {
  id: string;
  body: string;
  author: string;
  score?: number;
  depth?: number;
  is_submitter?: boolean;
}

// Advanced Reply (Set D framework) — string unions for editor autocomplete.
// Backend accepts plain strings, so these are non-strict — extra values are tolerated.
export type AdvancedIntent =
  | 'educate'
  | 'soft_recommend'
  | 'hard_recommend'
  | 'build_trust'
  | 'counter_argument'
  | 'story_persuasion';
export type AdvancedAudienceAwareness =
  | 'unaware'
  | 'problem_aware'
  | 'solution_aware'
  | 'product_aware';
export type AdvancedPersona = 'expert' | 'real_user' | 'founder' | 'neutral_observer';
export type AdvancedDepth = 'short' | 'medium' | 'deep';
export type AdvancedReplyFormat = 'paragraph' | 'bullets' | 'story_insight' | 'step_by_step';
export type AdvancedMentionProduct = 'none' | 'indirect' | 'direct';
export type AdvancedCtaType = 'none' | 'question' | 'soft_suggestion';
export type AdvancedToneOverlay = 'casual' | 'analytical' | 'opinionated';
export type AdvancedStrategyPreset =
  | 'safe'
  | 'high_engagement'
  | 'conversion_focused'
  | 'authority_building'
  | 'viral_controversial';
export type AdvancedVariantStyle = 'safe' | 'engaging' | 'persuasive';

export interface AdvancedReplyControls {
  user_custom_intent?: string;
  strategy_preset?: AdvancedStrategyPreset;
  intent?: AdvancedIntent;
  audience_awareness?: AdvancedAudienceAwareness;
  persona?: AdvancedPersona;
  depth?: AdvancedDepth;
  reply_format?: AdvancedReplyFormat;
  mention_product?: AdvancedMentionProduct;
  cta_type?: AdvancedCtaType;
  advanced_tone?: AdvancedToneOverlay;
  variant_style?: AdvancedVariantStyle;
  is_advanced?: boolean;
}

export interface AIReplyGenerateRequest extends AdvancedReplyControls {
  post_content: string;
  post_title: string;
  post_url?: string;
  // Subreddit name (without r/ prefix) - used to fetch and apply community
  // guidelines so the generated reply respects subreddit rules.
  subreddit?: string;
  brand_info?: string;
  brand_id?: string;
  custom_wording?: string;
  additional_context?: string;
  tone_preference?: string;
  // Comment reply context (optional - for replying to specific comments)
  target_comment?: CommentContext;
  parent_comments?: CommentContext[];
  sibling_comments?: CommentContext[];
  // When true, override the auto-softening of brand mention in promo-restricted
  // subreddits. The reply may then be at higher risk of moderator removal.
  force_brand_mention?: boolean;
}

export interface AIReplyGenerateResponse {
  success: boolean;
  stage1: string;
  stage2: string;
  stage3: string;
  final_reply: string;
  metadata: {
    post_title: string;
    brand_info?: string;
    custom_wording?: string;
    tone_preference: string;
    reply_type?: 'post' | 'comment';
    target_comment_author?: string;
    subreddit?: string | null;
    guidelines_applied?: boolean;
    guidelines_rule_count?: number;
    guidelines_promo_restricted?: boolean;
    // Advanced Reply (Set D) — only populated when is_advanced=true
    is_advanced?: boolean;
    strategy_preset?: AdvancedStrategyPreset | null;
    intent?: AdvancedIntent | null;
    audience_awareness?: AdvancedAudienceAwareness | null;
    persona?: AdvancedPersona | null;
    depth?: AdvancedDepth | null;
    reply_format?: AdvancedReplyFormat | null;
    mention_product?: AdvancedMentionProduct | null;
    cta_type?: AdvancedCtaType | null;
    advanced_tone?: AdvancedToneOverlay | null;
    variant_style?: AdvancedVariantStyle | null;
    user_custom_intent_used?: boolean;
  };
  validation_passed?: boolean;
  retry_count?: number;
  warnings?: string[] | null;
}

export interface AIReplyRegenerateRequest extends AdvancedReplyControls {
  stage: number;
  post_content: string;
  post_title: string;
  post_url?: string;
  subreddit?: string;
  previous_stages: Record<string, string>;
  brand_info?: string;
  custom_wording?: string;
  additional_context?: string;
  tone_preference?: string;
  force_brand_mention?: boolean;
}

export interface SubredditRule {
  short_name: string;
  description: string;
  kind?: string;
  priority?: number | null;
}

export interface SubredditGuidelinesResponse {
  subreddit: string;
  rule_count: number;
  rules: SubredditRule[];
  site_rules: string[];
  public_description: string;
  submission_type?: string | null;
  subreddit_type?: string | null;
  restricts_promotion: boolean;
  fetch_error?: string | null;
  available: boolean;
}

export interface AIReplyBatchRequest extends AIReplyGenerateRequest {
  num_variations: number;
}

export interface AIReplyBatchResponse {
  success: boolean;
  variations: AIReplyGenerateResponse[];
  total_generated: number;
}

// --- Auto-strategy recommender (Advanced Reply pre-fill) -----------------
export interface AIReplyRecommendRequest {
  post_title: string;
  post_content: string;
  post_url?: string;
  subreddit?: string;
  brand_info?: string;
  brand_id?: string;
  target_comment?: CommentContext;
  parent_comments?: CommentContext[];
  user_custom_intent?: string;
}

export interface AIReplyRecommendResponse {
  strategy_preset: AdvancedStrategyPreset;
  intent: AdvancedIntent;
  audience_awareness: AdvancedAudienceAwareness;
  persona: AdvancedPersona;
  depth: AdvancedDepth;
  reply_format: AdvancedReplyFormat;
  mention_product: AdvancedMentionProduct;
  cta_type: AdvancedCtaType;
  advanced_tone: AdvancedToneOverlay;
  rationale: string;
  subreddit_guidelines_applied: boolean;
  brand_context_used: boolean;
}

class AIReplyService {
  async generateReply(request: AIReplyGenerateRequest): Promise<AIReplyGenerateResponse> {
    try {
      return await api.post<AIReplyGenerateResponse>(`${API_URL}/generate`, request, {
        timeoutMs: GENERATE_TIMEOUT_MS,
      });
    } catch (error: any) {
      console.error('Error generating AI reply:', error);
      throw error;
    }
  }

  async regenerateStage(request: AIReplyRegenerateRequest): Promise<{ success: boolean; stage: number; output: string }> {
    try {
      return await api.post<{ success: boolean; stage: number; output: string }>(
        `${API_URL}/regenerate-stage`,
        request,
        { timeoutMs: GENERATE_TIMEOUT_MS }
      );
    } catch (error: any) {
      console.error('Error regenerating stage:', error);
      throw error;
    }
  }

  async batchGenerateReplies(request: AIReplyBatchRequest): Promise<AIReplyBatchResponse> {
    try {
      return await api.post<AIReplyBatchResponse>(`${API_URL}/batch-generate`, request, {
        timeoutMs: GENERATE_TIMEOUT_MS,
      });
    } catch (error: any) {
      console.error('Error batch generating replies:', error);
      throw error;
    }
  }

  /**
   * Advanced Reply wizard helper.
   *
   * Calls the existing /batch-generate endpoint with `is_advanced: true` and
   * `num_variations: 3`. The backend will overlay variant_style="safe",
   * "engaging", and "persuasive" on the three returned variations so users
   * get three meaningfully different versions in one call.
   */
  async generateAdvancedVariants(
    request: AIReplyGenerateRequest
  ): Promise<AIReplyBatchResponse> {
    return this.batchGenerateReplies({
      ...request,
      is_advanced: true,
      num_variations: 3,
    });
  }

  /**
   * Auto-suggest the best Set D advanced reply controls (strategy preset,
   * intent, persona, depth, format, mention, CTA, tone) for the given post
   * using a single LLM call. Used by the Advanced AI Reply modal to pre-fill
   * its pills when it opens.
   */
  async recommendStrategy(
    request: AIReplyRecommendRequest
  ): Promise<AIReplyRecommendResponse> {
    return await api.post<AIReplyRecommendResponse>(
      `${API_URL}/recommend-strategy`,
      request
    );
  }

  async checkHealth(): Promise<{ status: string; message: string; ready: boolean }> {
    try {
      return await api.get<{ status: string; message: string; ready: boolean }>(`${API_URL}/health`);
    } catch (error: any) {
      console.error('Error checking AI service health:', error);
      return {
        status: 'error',
        message: 'Failed to check service health',
        ready: false,
      };
    }
  }

  /**
   * Fetch cached community guidelines for a subreddit so the UI can show
   * which rules will be applied when generating an AI reply.
   * Strips a leading "r/" if the caller passed it in.
   */
  async getSubredditGuidelines(subreddit: string): Promise<SubredditGuidelinesResponse> {
    const normalized = subreddit
      .trim()
      .replace(/^\/+/, '')
      .replace(/^r\//i, '')
      .replace(/\/+$/, '');
    return await api.get<SubredditGuidelinesResponse>(
      `/api/subreddit-rules/${encodeURIComponent(normalized)}`
    );
  }
}

const aiReplyService = new AIReplyService();
export default aiReplyService;
